/* eslint-disable @typescript-eslint/no-unused-vars */

import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { SubscriptionHandle } from '@jack-henry/frontend-utils/observable';
import { LoggingService } from '@treasury/core/logging';
import { NavigationService } from '@treasury/core/navigation';
import {
    RemoteDepositBatch,
    RemoteDepositCaptureService,
    RemoteDepositDataField,
    RemoteDepositLocation,
} from '@treasury/domain/remote-deposit-capture';
import { defaultItemModel } from '@treasury/domain/remote-deposit-capture/data/item.data';
import { RemoteDepositError } from '@treasury/domain/remote-deposit-capture/remote-deposit-error';
import { CheckCaptureService } from '@treasury/libraries';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/tm-confirm-dialog';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { LayoutService } from '../../services/layout.service';
import '../partials/capture-step';
import '../partials/deposit-confirmation-step';
import '../partials/deposit-create-step';
import '../partials/deposit-review-step';
import '../partials/location-selection-step';
import { DepositVm, RemoteDepositItemVm } from '../view-models';
import {
    AddCheckEvent,
    CancelCaptureEvent,
    DeleteDepositEvent,
    FinishedDepositEvent,
    LocationSelectedEvent,
    NewDepositEvent,
    ProceedToCreateEvent,
    ProceedToReviewEvent,
    RemoveCheckEvent,
    SubmitDepositEvent,
} from './remote-deposit.types';

enum DepositStep {
    SelectLocation = 1,
    Capture,
    Create,
    Review,
    Confirm,
}

export type DepositEvent = CustomEvent<RemoteDepositItemVm>;

export const tagName = 'deposit-container';
@customElement(tagName)
export class DepositContainer extends TmContainer {
    @InjectProperty()
    declare private readonly layoutService: LayoutService;

    @InjectProperty()
    declare private readonly remoteDepositCaptureService: RemoteDepositCaptureService;

    @InjectProperty()
    declare private readonly checkCaptureService: CheckCaptureService;

    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly logService: LoggingService;

    @state()
    private dataFields?: RemoteDepositDataField[];

    @state()
    private currentStep: DepositStep = DepositStep.SelectLocation;

    @state()
    private formData: RemoteDepositItemVm = new RemoteDepositItemVm({ ...defaultItemModel });

    @state()
    private location?: RemoteDepositLocation;

    @state()
    private deposit?: DepositVm;

    @state()
    private heading = '';

    @state()
    private headerSubtitle = '';

    @state()
    private checkDetailOpen = false;

    @state()
    private isAddingCheck = false;

    @state()
    private showConfirmBackDialog = false;

    @state()
    private hideHeader = false;

    private sdkErrorSubscription?: SubscriptionHandle;

    public disconnectedCallback() {
        super.disconnectedCallback();
        this.checkCaptureService.removeListeners();
        // this.sdkErrorSubscription?.unsubscribe();
        if (this.currentStep !== DepositStep.Confirm) {
            this.logService.logInfo('Abandoning deposit', {
                capture_step: this.currentStep,
            });
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.sdkErrorSubscription = this.checkCaptureService.sdkError$.subscribe(error => {
            if (error.type !== 'CAMERA_PERMISSION_DENIED') {
                this.notificationService.renderError(new Error(`Mitek SDK Error: ${error.type}`));
            }
        });
    }

    protected async firstUpdated() {
        const { params, url } = await this.navService.getRouteData<{
            id: string;
            deposit: RemoteDepositBatch;
        }>();
        const selectedBatchReference = params.id;
        if (url.includes('edit') && selectedBatchReference) {
            const selectedDeposit = await this.remoteDepositCaptureService.hydrateDeposit(
                selectedBatchReference,
                params.deposit
            );
            if (selectedDeposit) {
                this.logService.logInfo('Editing existing deposit', {
                    capture_step: 'Review',
                });
                this.deposit = new DepositVm(selectedDeposit, selectedDeposit.location);
                this.location = selectedDeposit.location;
                this.currentStep = DepositStep.Review;
            }
        }

        this.configureHeader();
        await this.checkCaptureService.init();
        this.loading = false;
    }

    protected render() {
        return html`
            ${this.renderLoader()} ${this.renderStep()} ${this.renderConfirmBackDialog()}
        `;
    }

    private handleBackAction() {
        // Get the selected enum value minus one
        if (this.currentStep === DepositStep.SelectLocation) {
            this.navService.navigate(`/deposit`);
        } else if (this.currentStep === DepositStep.Review) {
            this.showConfirmBackDialog = true;
        } else {
            this.currentStep -= 1;
        }
    }

    private configureHeader() {
        this.headerService.configure({
            title: this.heading,
            subtitle: this.headerSubtitle,
            backAction: () => this.handleBackAction(),
            hideHeader: this.hideHeader,
            enableFdicSign: true,
        });
    }

    private async submitDeposit(e: SubmitDepositEvent) {
        if (!this.deposit || !this.deposit.batchReference) {
            throw new Error('Could not submit deposit. None was available or it was invalid.');
        }

        this.loading = true;

        try {
            this.logService.logInfo('Submitting deposit to API', {
                capture_step: 'Review',
            });
            const response = await this.remoteDepositCaptureService.closeDeposit(
                this.deposit.batchReference
            );

            if (response && response.status) {
                this.logService.logInfo('Deposit successful', {
                    capture_step: 'Review',
                });
                const { status } = response;
                this.deposit.status = status;
                this.currentStep = DepositStep.Confirm;
            }
        } catch (err) {
            this.logService.logError(
                'Remote Deposit Error - closeDeposit',
                err as RemoteDepositError
            );
            this.notificationService.renderError(err as RemoteDepositError);
        } finally {
            this.loading = false;
        }
    }

    private async deleteDeposit(e: DeleteDepositEvent) {
        if (!this.deposit || !this.deposit.batchReference) {
            throw new Error('Could not submit deposit. None was available or it was invalid.');
        }

        this.loading = true;

        try {
            this.logService.logInfo('Starting delete deposit API', {
                capture_step: 'Review',
            });
            const response = await this.remoteDepositCaptureService.deleteDeposit(
                this.deposit.batchReference
            );
            if (response) {
                this.logService.logInfo('Deposit deleted successfully', {
                    capture_step: 'Review',
                });
                this.navService.navigate(`/deposit`);
            }
        } catch (err) {
            this.logService.logError(
                'Remote Deposit Error - deleteDeposit',
                err as RemoteDepositError
            );
            this.notificationService.renderError(err as RemoteDepositError);
        } finally {
            this.loading = false;
        }
    }

    private async getDataFields(locationReference: string) {
        // We're keeping a local dataFields variable because that won't change between adding checks.
        try {
            this.dataFields =
                await this.remoteDepositCaptureService.getDataFields(locationReference);
        } catch (err) {
            // We don't want to block the user from proceeding if we can't get the data fields.
            // The API returns an error state if the location doesn't have any data fields.
            this.dataFields = undefined;
        }
    }

    private async proceedToCaptureStep(location: RemoteDepositLocation) {
        this.loading = true;
        this.logService.logInfo('Starting capture step', {
            capture_step: 'SelectLocation',
            location_reference: location.locationReference,
        });
        if (!location?.locationReference) {
            this.logService.logError(
                'Remote Deposit Error - empty location reference',
                new Error()
            );
            this.notificationService.renderError(
                new RemoteDepositError('Invalid Location Reference')
            );
            this.loading = false;
            this.currentStep = DepositStep.SelectLocation;
            return;
        }
        this.location = location;
        this.logService.logInfo('Getting data fields', {
            capture_step: 'SelectLocation',
            location_reference: location.locationReference,
        });
        await this.getDataFields(location.locationReference);

        try {
            const openBatch = await this.remoteDepositCaptureService.openDeposit(location);
            if (openBatch) {
                this.deposit = new DepositVm(openBatch, location);
            }
            this.formData.dataFields = this.dataFields ?? [];
            this.logService.logInfo('Data fields received', {
                capture_step: 'SelectLocation',
                location_reference: location.locationReference,
            });
            this.currentStep = DepositStep.Capture;
        } catch (err) {
            this.logService.logError(
                'Remote Deposit Error - openDeposit',
                err as RemoteDepositError
            );
            this.notificationService.renderError(err as RemoteDepositError);
        } finally {
            this.loading = false;
        }
    }

    private proceedToCreateStep(e: ProceedToCreateEvent) {
        this.formData.frontImage = e.detail.frontImage ?? '';
        this.formData.rearImage = e.detail.rearImage ?? '';
        this.currentStep = DepositStep.Create;
        this.isAddingCheck = false;
    }

    private async proceedToReviewStep(check: RemoteDepositItemVm) {
        if (!this.deposit || !this.deposit.batchReference) {
            throw new Error('Could not submit deposit. None was available or it was invalid.');
        }

        this.loading = true;

        try {
            if (!this.location) {
                throw new Error('Location is not set');
            }
            const addItemResponse = await this.remoteDepositCaptureService.addItem(
                check.toAddItemRequestModelDto(this.location),
                this.deposit.batchReference
            );
            if (addItemResponse) {
                if (!addItemResponse.itemReference) {
                    throw new Error('No Item Reference was returned from the api');
                }
                check.itemReference = addItemResponse.itemReference;
                this.deposit?.addCheck(check);
                this.currentStep = DepositStep.Review;
            }
        } catch (err) {
            this.logService.logError('Remote Deposit Error - addItem', err as RemoteDepositError);
            this.notificationService.renderError(err as RemoteDepositError);
        } finally {
            this.loading = false;
        }
    }

    private finishedDeposit(e: FinishedDepositEvent) {
        this.navService.navigate(`/deposit`);
    }

    private async addNewCheck(e: AddCheckEvent) {
        this.isAddingCheck = true;
        if (!this.location?.locationReference) {
            return;
        }
        // We might have to load the data fields if the user came in through the edit route, since they are normally loaded after the select location step.
        if (!this.dataFields) {
            this.loading = true;
            await this.getDataFields(this.location.locationReference);
            this.loading = false;
        }

        this.formData = new RemoteDepositItemVm({ ...defaultItemModel });
        const dataFields = this.dataFields?.map(
            df => new RemoteDepositDataField({ ...df.toDto(), value: '' })
        );
        this.formData.dataFields = dataFields ?? [];
        this.currentStep = DepositStep.Capture;
    }

    private startNewDeposit(e: NewDepositEvent) {
        this.configureHeader();
        this.deposit = undefined;
        this.formData = new RemoteDepositItemVm({ ...defaultItemModel });
        this.logService.logInfo('New Deposit', { capture_step: 'SelectLocation' });
        this.currentStep = DepositStep.SelectLocation;
    }

    private async deleteCheck(itemReference: string) {
        try {
            this.checkDetailOpen = true;
            await this.remoteDepositCaptureService.deleteItem(itemReference);
            this.deposit?.deleteCheck(itemReference);
        } catch (err) {
            this.logService.logError(
                'Remote Deposit Error - deleteCheck',
                err as RemoteDepositError
            );
            this.notificationService.renderError(err as RemoteDepositError);
        } finally {
            this.checkDetailOpen = false;
        }
    }

    private renderConfirmBackDialog() {
        if (!this.showConfirmBackDialog) {
            return nothing;
        }
        return html`<tm-confirm-dialog
            .opened=${this.showConfirmBackDialog}
            header="Confirm Navigation"
            message="Continuing back will leave your current deposit open and return to the deposits view. Is that what you want to do?"
            confirmText="Yes"
            confirmTheme="error"
            cancel
            cancelText="No"
            @confirm=${() => {
                this.showConfirmBackDialog = false;
                this.navService.navigate(`/deposit`);
            }}
            @cancel=${() => {
                this.showConfirmBackDialog = false;
            }}
        ></tm-confirm-dialog>`;
    }

    private renderStep() {
        switch (this.currentStep) {
            case DepositStep.SelectLocation:
                this.headerService.title = 'Select Location';
                this.headerService.subtitle = '';
                this.layoutService.displayLandscapeWarningMessage(true);
                return html`
                    <location-selection-step
                        @location-selected=${(e: LocationSelectedEvent) =>
                            this.proceedToCaptureStep(e.detail)}
                    >
                    </location-selection-step>
                `;
            case DepositStep.Capture:
                this.logService.logInfo('Data fields received', { capture_step: 'Capture' });
                this.hideHeader = true;
                this.configureHeader();
                this.layoutService.displayLandscapeWarningMessage(false);
                return html`
                    <capture-step
                        @proceed-to-create=${(e: ProceedToCreateEvent) => {
                            this.logService.logInfo('Images captured, proceeding to create step', {
                                capture_step: 'Capture',
                            });
                            this.hideHeader = false;
                            this.configureHeader();
                            this.proceedToCreateStep(e);
                        }}
                        @cancel-capture=${(e: CancelCaptureEvent) => {
                            this.logService.logInfo('Images capture canceled', {
                                capture_step: 'Capture',
                            });
                            this.hideHeader = false;
                            this.configureHeader();
                            if (this.isAddingCheck) {
                                this.currentStep = DepositStep.Review;
                            } else {
                                this.currentStep = DepositStep.SelectLocation;
                            }
                        }}
                        @image-upload-failed=${(e: CustomEvent) => {
                            this.logService.logError('Images capture canceled', new Error());
                            this.notificationService.renderError(
                                new RemoteDepositError('Image upload failed: ', e.detail)
                            );
                        }}
                    ></capture-step>
                `;
            case DepositStep.Create:
                this.logService.logInfo('Starting create step', { capture_step: 'Create' });
                this.headerService.title = 'Create Deposit';
                this.headerService.subtitle = this.deposit?.locationName ?? '';
                this.layoutService.displayLandscapeWarningMessage(true);
                return html`
                    <deposit-create-step
                        .formData=${this.formData}
                        @proceed-to-review=${(e: ProceedToReviewEvent) =>
                            this.proceedToReviewStep(e.detail)}
                    ></deposit-create-step>
                `;
            case DepositStep.Review:
                this.logService.logInfo('Starting review step', { capture_step: 'Review' });
                this.headerService.title = 'Review Deposit';
                this.headerService.subtitle = '';
                return html`
                    <deposit-review-step
                        .deposit=${this.deposit}
                        @add-check=${(e: AddCheckEvent) => this.addNewCheck(e)}
                        @remove-check=${(e: RemoveCheckEvent) =>
                            this.deleteCheck(e.detail.itemReference)}
                        @submit-deposit=${(e: SubmitDepositEvent) => this.submitDeposit(e)}
                        @delete-deposit=${(e: DeleteDepositEvent) => this.deleteDeposit(e)}
                        .checkDetailOpen=${this.checkDetailOpen}
                    ></deposit-review-step>
                `;
            case DepositStep.Confirm:
                this.logService.logInfo('Showing confirmation view', { capture_step: 'Confirm' });
                this.headerService.title = 'Deposit Confirmation Receipt';
                this.headerService.backAction = undefined;
                return html`
                    <deposit-confirmation-step
                        .deposit=${this.deposit}
                        @finished-deposit=${(e: FinishedDepositEvent) => this.finishedDeposit(e)}
                        @new-deposit=${(e: NewDepositEvent) => this.startNewDeposit(e)}
                    ></deposit-confirmation-step>
                `;
            default:
                return nothing;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: grid;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DepositContainer;
    }
}
